import { css, html, LitElement, nothing } from 'lit';
import '../../library/elements/d-wrap.js';
import { customElement, property } from 'lit/decorators.js';
import '../../library/fields/d-expansion.js';
import '../../library/fields/d-view-text.js';
import '../../library/fields/d-view-info.js';
import '../../library/elements/d-label.js';
import '../../library/editors/components/d-select-time.js';
import { produce } from 'immer';
import type { WorkScheduleWeek } from '@trinnvis/dabih-api-client';
import { forDayIndex, timeInMinutes } from 'src/store/selectors';
import { minutesToTime } from 'src/utilities/text.js';
import type { WorkScheduleRestPeriodEnum } from '@trinnvis/dabih-api-client/src/models/WorkSchedule';
import { t, tArray } from 'src/i18n';

/**
 *
 */
@customElement('d-edit-work-weeks')
export class DEditWorkWeeks extends LitElement {
  static readonly styles = [
    css`
      :host {
        display: block;
      }

      d-label {
        margin-bottom: 6px;
      }

      d-view-info {
        margin: -6px 0 8px 0;
      }

      .weekdays {
        display: flex;
        margin: 0 0 8px 1px;
      }

      .day {
        min-width: 46px;
        max-width: 58px;
        margin-left: -1px;
      }

      .sum {
        margin: 6px 0 0 0;
      }

      .day-name,
      d-select-time {
        border: 1px solid hsl(1, 0%, 80%);
        margin-top: -1px;
        text-align: center;
      }

      .day-name {
        font-size: 12px;
        letter-spacing: 1px;
        padding: 8px 0;
        background-color: hsla(1, 0%, 0%, 0.1);
        color: hsl(1, 0%, 70%);
        cursor: pointer;
      }

      .day-name.workday {
        background-color: white;
        color: hsl(1, 0%, 20%);
      }

      d-select-time {
        font-size: 14px;
        display: flex;
        justify-content: center;
        align-items: baseline;
      }
    `,
  ];

  @property({ type: String })
  restPeriod: WorkScheduleRestPeriodEnum = 'WITHOUT_PAY';
  @property({ type: Array })
  workWeeks!: WorkScheduleWeek[];
  @property({ type: String })
  workHoursType = '';
  @property({ type: String })
  label = '';

  _computeWeekNumber(weekIndex: number) {
    if (this.workWeeks.length > 1) {
      return t('staffing.weekNumber', { number: weekIndex + 1 });
    }
    return t('staffing.workWeek');
  }

  _dayName(index: number) {
    return tArray('staffing.weekdaysShortUpper')[index];
  }

  _workdayClass(week: WorkScheduleWeek, n: number) {
    if (forDayIndex(week, n).workDay) {
      return 'day-name workday';
    }
    return 'day-name';
  }

  _toggleSelect(_e: CustomEvent, dayIndex: number, weekIndex: number) {
    this.workWeeks = produce(this.workWeeks, (draft) => {
      const day = forDayIndex(draft[weekIndex], dayIndex);
      day.workDay = !day.workDay;
      // Set default start/end times when enabling a work day
      if (day.workDay && (!day.start || !day.end)) {
        day.start = '07:00';
        day.end = '15:30';
      }
    });
    this.dispatchEvent(
      new CustomEvent('work-weeks-changed', { composed: true, bubbles: true, detail: { workWeeks: this.workWeeks } }),
    );
  }

  _showTime(workHoursType: string, week: WorkScheduleWeek, n: number) {
    return workHoursType !== 'default' && forDayIndex(week, n).workDay;
  }

  _sum(week: WorkScheduleWeek): number {
    let result = 0;
    for (const day in week) {
      if (week[day].workDay) {
        const start = timeInMinutes(week[day].start);
        const end = timeInMinutes(week[day].end);
        let hours = (end - start) / 60;
        if (this.restPeriod === 'WITHOUT_PAY' && hours >= 5) {
          hours -= 0.5;
        }
        result += hours;
      }
    }
    return Number(result.toFixed(2));
  }

  _restPeriodComment(week: WorkScheduleWeek): string {
    if (this.restPeriod === 'WITHOUT_PAY') {
      for (const day in week) {
        if (week[day].workDay) {
          const start = timeInMinutes(week[day].start);
          const end = timeInMinutes(week[day].end);
          const hours = (end - start) / 60;
          if (hours < 5) {
            return t('staffing.lunchBreakNotDeductedShortDays');
          }
        }
      }
    }
    return ``;
  }

  onUpdateWeekDayStartTime(weekIndex: number, dayIndex: number, newValue: string) {
    this.workWeeks = produce(this.workWeeks, (draft) => {
      forDayIndex(draft[weekIndex], dayIndex).start = newValue;
    });
    this.dispatchEvent(
      new CustomEvent<{ workWeeks: WorkScheduleWeek[] }>('work-weeks-changed', {
        composed: true,
        bubbles: true,
        detail: { workWeeks: this.workWeeks },
      }),
    );
    const day = forDayIndex(this.workWeeks[weekIndex], dayIndex);
    if (day.start && day.end) {
      const startMinutes = timeInMinutes(day.start);
      const endMinutes = timeInMinutes(day.end);
      if (endMinutes - startMinutes < 5) {
        this.onUpdateWeekDayEndTime(weekIndex, dayIndex, minutesToTime(startMinutes + 5));
      }
    }
  }

  onUpdateWeekDayEndTime(weekIndex: number, dayIndex: number, newValue: string) {
    this.workWeeks = produce(this.workWeeks, (draft) => {
      forDayIndex(draft[weekIndex], dayIndex).end = newValue;
    });
    this.dispatchEvent(
      new CustomEvent<{ workWeeks: WorkScheduleWeek[] }>('work-weeks-changed', {
        composed: true,
        bubbles: true,
        detail: { workWeeks: this.workWeeks },
      }),
    );
    const day = forDayIndex(this.workWeeks[weekIndex], dayIndex);
    if (day.start && day.end) {
      const startMinutes = timeInMinutes(day.start);
      const endMinutes = timeInMinutes(day.end);
      if (endMinutes - startMinutes < 5) {
        this.onUpdateWeekDayStartTime(weekIndex, dayIndex, minutesToTime(endMinutes - 5));
      }
    }
  }

  render() {
    return html`
      ${this.workWeeks.map(
        (week, weekIndex) => html`
          ${this.label
            ? html` <d-label .label=${this.label}></d-label> `
            : html`
                <d-view-text
                  wrap-label
                  label="${this._computeWeekNumber(weekIndex)}"
                  value="${t('staffing.hoursSuffix', { count: this._sum(week) })}"
                ></d-view-text>
                ${this._restPeriodComment(week)
                  ? html` <d-view-info mini .content=${this._restPeriodComment(week)}> </d-view-info>`
                  : nothing}
              `}
          <div class="weekdays">
            ${this.renderDay(week, weekIndex, week.monday, 0)} ${this.renderDay(week, weekIndex, week.tuesday, 1)}
            ${this.renderDay(week, weekIndex, week.wednesday, 2)} ${this.renderDay(week, weekIndex, week.thursday, 3)}
            ${this.renderDay(week, weekIndex, week.friday, 4)} ${this.renderDay(week, weekIndex, week.saturday, 5)}
            ${this.renderDay(week, weekIndex, week.sunday, 6)}
          </div>
        `,
      )}
    `;
  }

  private renderDay(week, weekIndex, day, dayIndex) {
    return html`
      <div class="day">
        <div
          class="${this._workdayClass(week, dayIndex)}"
          @click=${(e: CustomEvent) => this._toggleSelect(e, dayIndex, weekIndex)}
        >
          ${this._dayName(dayIndex)}
        </div>
        <d-expansion ?opened=${this._showTime(this.workHoursType, week, dayIndex)}>
          <d-select-time
            small
            max="23:50"
            .value=${day.start}
            @value-changed=${(e: CustomEvent) => this.onUpdateWeekDayStartTime(weekIndex, dayIndex, e.detail.value)}
          ></d-select-time>
          <d-select-time
            small
            min="00:05"
            .value=${day.end}
            @value-changed=${(e: CustomEvent) => this.onUpdateWeekDayEndTime(weekIndex, dayIndex, e.detail.value)}
          ></d-select-time>
        </d-expansion>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-edit-work-weeks': DEditWorkWeeks;
  }
}
